import { Injectable } from '@angular/core';
import { EntityDefinitionService, EntityServices, QueryParams } from '@ngrx/data';
import { GetOptions, PaginatedEntityService } from '@app/data/paginated-entity.service';
import { DateTime, Duration } from 'luxon';
import { olderThan } from '@app/shared/rxjs/operators/standard-operators';
import { distinctUntilChanged, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { CcoEntityMetadata } from '@app/data/entity-metadata';
import { combineLatest, Observable, Subject } from 'rxjs';
import { Dictionary } from '@ngrx/entity';
import { Age, EntityService } from '@core/services/entity.service';
import { select, Store } from '@ngrx/store';
import { selectGlobalFiltersState, State } from '@app/store/reducers';

@Injectable()
export class DefaultEntityService implements EntityService {
  private loadRequestInFlight: {
    [entityName: string]: boolean;
  } = {};

  constructor(
    protected store: Store<State>,
    protected entityServices: EntityServices,
    protected paginatedEntityService: PaginatedEntityService,
    protected entityDefinitionService: EntityDefinitionService,
  ) {}

  getEcs<T>(entityName: string) {
    return this.entityServices.getEntityCollectionService<T>(entityName);
  }

  add<T>(entityName: string, entity: T): Observable<T> {
    return this.getEcs<T>(entityName).add(entity);
  }

  entities$<T>(entityName: string) {
    return this.getEcs<T>(entityName).entities$;
  }

  entityMap$<T>(entityName: string) {
    return this.getEcs<T>(entityName).entityMap$;
  }

  load<T>(entityName: string): Observable<T[]> {
    if (this.entityIsPaged(entityName)) {
      this.paginatedEntityService.getAll(entityName);
      return this.entities$<T>(entityName);
    } else
      return this.getEcs<T>(entityName).load();
  }

  /**
   * If a load is triggered because the currently loaded data is stale, return an Observable of the
   * loaded data; otherwise, return an Observable of the currently loaded data.
   */
  loadIfOlderThan<T>(
    entityName: string,
    age: Age,
    maxPages?: number,
  ): Observable<T[]> {
    if (this.loadRequestInFlight[entityName]) {
      return this.entities$<T>(entityName).pipe(
        take(1)
      );
    }
    this.loadRequestInFlight[entityName] = true;

    const loadedData$ = new Subject<T[]>();
    const thisEntityService = this.getEcs<T>(entityName);
    thisEntityService.selectors$.loadedAt$.pipe(
      olderThan(age),
      take(1),
      switchMap(() => {
        if (this.entityIsPaged(entityName)) {
          return this.getEntitiesWithPaginationBasedOnGlobalFilter<T>(entityName, {maxPages});
        }

        return thisEntityService.load();
      }),
    ).subscribe({
      next: (loadedData) => {
        loadedData$.next(loadedData);
        this.loadRequestInFlight[entityName] = false;
        loadedData$.complete();
      },
      complete: () => {
        if (!loadedData$.closed) {
          this.entities$<T>(entityName).pipe(
            take(1)
          ).subscribe((entities) => {
            loadedData$.next(entities);
            this.loadRequestInFlight[entityName] = false;
            loadedData$.complete();
          });
        }
      }
    });

    return loadedData$;
  }

  getWithQueryIfOlderThan<T>(
    entityName: string,
    query: QueryParams,
    age?: Age,
    ignoreGlobalFilter = false,
  ): Observable<T[]> {
    const results$ = new Subject<T[]>();

    const thisEntityService = this.getEcs<T>(entityName);
    thisEntityService.selectors$.queryLoadedAt$
      .pipe(
        map(queryLoadedAt => queryLoadedAt[JSON.stringify(query)]),
        olderThan(age),
        take(1),
        switchMap(() => {
          if (this.entityIsPaged(entityName)) {
            if (ignoreGlobalFilter) {
              return this.paginatedEntityService.getWithPagination<T>(entityName, { query }, true);
            }
            return this.getEntitiesWithPaginationBasedOnGlobalFilter<T>(entityName, { query });
          }

          return thisEntityService.getWithQuery(query);
        }),
      ).subscribe(results => results$.next(results));

    return results$.pipe(
      shareReplay(1),
    );
  }

  getTimeLastLoaded<T>(entityName: string): Observable<string> {
    const thisEntityService = this.getEcs<T>(entityName);

    return thisEntityService.selectors$.loadedAt$;
  }

  getWithQuery<T>(entityName: string, query: QueryParams, ignoreGlobalFilter = false): Observable<T[]> {
    return this.getWithQueryIfOlderThan<T>(entityName, query, null, ignoreGlobalFilter);
  }

  selectEntitiesAndLoadIfOlderThan<T>(
    entityName: string,
    age?: Age,
  ): Observable<T[]> {
    this.loadIfOlderThan(entityName, age);
    return this.entities$<T>(entityName);
  }

  selectEntityMapAndLoadIfOlderThan<T>(
    entityName: string,
    age?: Age,
  ): Observable<Dictionary<T>> {
    this.loadIfOlderThan(entityName, age);
    return this.entityMap$<T>(entityName);
  }

  loadEntitiesModifiedSinceLastLoad<T>(
    entityName: string,
  ) {
    combineLatest([
      this.store.pipe(select(selectGlobalFiltersState))
        .pipe(
          distinctUntilChanged((a, b) => a.startAtOrAfter === b.startAtOrAfter && a.endAtOrBefore === b.endAtOrBefore),
          map((filterState) => {
            if (filterState) {
              // https://stackoverflow.com/questions/286141/remove-blank-attributes-from-an-object-in-javascript
              return Object.entries(filterState).reduce((a,[k,v]) => (v == null ? a : (a[k]=v, a)), {});
            }
          }),
        ),
      (this.getEcs(entityName).selectors$.queryLoadedAt$ as Observable<Dictionary<string>>).pipe(take(1)),
      (this.getEcs(entityName).selectors$.loadedAt$ as Observable<string>).pipe(take(1)),
    ]).pipe(
      switchMap(([filterState, queryLoadedAt, loadedAt]) => {

        if (loadedAt != null && loadedAt !== '') {
          return this.paginatedEntityService.getWithPagination<T>(entityName, { query: {modifiedSince: loadedAt} });
        }

        if (!this.entityIsTimeBased(entityName)) {
          return this.paginatedEntityService.getWithPagination<T>(entityName);
        }

        const latestQueryMatchingParams = this.getMostRecentDataFetchWhichUsedTheGivenQueryParams(queryLoadedAt, filterState);
        if (latestQueryMatchingParams) {
          return this.paginatedEntityService.getWithPagination<T>(entityName, { query: { modifiedSince: latestQueryMatchingParams }, ...filterState});
        }

        return this.paginatedEntityService.getWithPagination<T>(entityName, filterState);
      }),
    ).subscribe();
  }

  private getEntitiesWithPaginationBasedOnGlobalFilter<T>(entityName: string, options: GetOptions = {}): Observable<T[]> {

    return this.store.pipe(select(selectGlobalFiltersState)).pipe(
      distinctUntilChanged((a, b) => a.startAtOrAfter === b.startAtOrAfter && a.endAtOrBefore === b.endAtOrBefore),
      map((filterState) => {
        if (filterState) {
          // https://stackoverflow.com/questions/286141/remove-blank-attributes-from-an-object-in-javascript
          return Object.entries(filterState).reduce((a,[k,v]) => (v == null ? a : (a[k]=v, a)), {});
        }
      }),
      switchMap((filterState) => {
        if (this.entityIsTimeBased(entityName)) {
          return this.paginatedEntityService.getWithPagination<T>(entityName, {...options, query: {...options.query, ...filterState}});
        }
        return this.paginatedEntityService.getWithPagination<T>(entityName, options);
      }),
    );
  }

  getEntitiesModifiedSinceOrLoadIfOlderThan<T>(entityName: string, age: Age, maxPages?: number): Observable<T[]> {
    if (this.loadRequestInFlight[entityName]) {
      return this.entities$<T>(entityName).pipe(
        take(1),
      );
    }

    const thisEntityService = this.getEcs(entityName);
    thisEntityService.selectors$.loadedAt$
      .pipe(
        take(1),
      )
      .subscribe((loadedAt) => {
        const now = DateTime.local();
        const lastLoadedDT = DateTime.fromISO(loadedAt);
        const lastLoadedPlusAge = lastLoadedDT.plus(typeof age === 'string' ? Duration.fromISO(age) : age ?? 0);

        if (lastLoadedPlusAge < now) {
          return this.loadIfOlderThan(entityName, age, maxPages);
        }

        const query = {
          modifiedSince: DateTime.fromISO(loadedAt).toUTC().toISO(),
        };

        if (this.entityIsPaged(entityName)) {
          if (query.modifiedSince != null) {
            return this.paginatedEntityService.getWithPagination(entityName, { query })
              .pipe(tap(() => this.loadRequestInFlight[entityName] = false));
          } else {
            // if loadedAt was null, get without query
            return this.paginatedEntityService.getWithPagination(entityName)
              .pipe(tap(() => this.loadRequestInFlight[entityName] = false));
          }
        } else {
          return thisEntityService.getWithQuery(query)
            .pipe(tap(() => this.loadRequestInFlight[entityName] = false));
        }
      });
  }

  entityIsPaged(entityName: string): boolean {
    const entityMetadata = this.entityDefinitionService.getDefinition(entityName)
      .metadata as CcoEntityMetadata;

    return entityMetadata?.paged ?? false;
  }

  entityIsTimeBased(entityName: string): boolean {
    const entityMetadata = this.entityDefinitionService.getDefinition(entityName)
      .metadata as CcoEntityMetadata;

    return entityMetadata.timeBased ?? false;
  }

  private getMostRecentDataFetchWhichUsedTheGivenQueryParams(queryLoadedAt: Dictionary<string>, globalFilterState: Dictionary<string>): string {
    let latest: DateTime = null;

    if (globalFilterState) {
      const globalFilterKeys = Object.keys(globalFilterState);
      for (const queryLoadedAtKey of Object.keys(queryLoadedAt)) {
        const keyObj = JSON.parse(queryLoadedAtKey);

        if (globalFilterKeys.every(k => keyObj[k]) && (latest == null || latest < DateTime.fromISO(queryLoadedAt[queryLoadedAtKey]))) {
          latest = DateTime.fromISO(queryLoadedAt[queryLoadedAtKey]);
        }
      }
    }
    return latest?.toString() ?? null;
  }
}
